/**
 * @fileOverview Displays a table of data.
 */

import React from 'react'
import classnames from 'classnames'
import injectStyles from 'react-jss'
import { Table as TableC } from 'Components/Table'
import { Td } from 'Components/Table/Td'
import { Tr } from 'Components/Table/Tr'
import './types'
import SortableHeader from './SortableHeader'
import HoverWrapper from '../HoverWrapper'
import QuestionMark from '../Icons/QuestionMark'

const styles = {
  table: {
    cursor: 'pointer',
    '@global': {
      'td:last-child, th:last-child': {
        paddingRight: 65,
      },
      'td:first-child, th:first-child': {
        paddingLeft: 65,
      },
    },
  },
  tableHeader: {
    position: 'sticky',
    top: 0,
    background: '#FFFFFF',
    fontFamily: 'Proxima Nova, Helvetica, Arial, sans-serif',
    zIndex: 1,
  },
  rowWarning: {
    backgroundColor: 'rgba(255, 206, 5, 0.2) !important',
  },
  noOpacity: {
    opacity: 1,
  },
  paddingRight: { paddingLeft: '0 !important', paddingRight: 65 },
  hoverLine: {
    width: 6,
    height: 48,
    borderRadius: 2,
  },
  firstCellContainer: {
    position: 'relative',
  },
  groupedDataOverlay: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}

/**
 * @typedef TableProps
 * @property {Array<SortableHeader>} header The column headers
 * @property {Array<Array<string>>} data Multi dimensional array to equate to
 * the table rows of data to display
 * @property {Array<Function>} actions If specified will fire the action when
 * the related row is tapped/clicked.
 *
 * @param {TableProps} props
 */

const rating = (
  <HoverWrapper
    style={{ maxWidth: 450 }}
    title="Rating Helper"
    hoverText={
      <div>
        <div>
          <strong>How is the Rating Calculated?</strong>
        </div>
        <br />
        <div>
          Mission ratings measure how successful a mission was. They are based
          on three key results, measured <strong>7 days after</strong> the
          mission is completed:
        </div>
        <div>
          <ul>
            <li>
              <strong>Conversions:</strong> How many desired actions resulted
              from the flyers (accounts for 45% of the rating).
            </li>
            <li>
              <strong>Scan:</strong> How many QR codes or links were
              scanned/used (accounts for 45% of the rating).
            </li>
            <li>
              <strong>Flyers Distributed:</strong> The percentage of the
              assigned flyers that were actually given out (accounts for 10% of
              the rating).
            </li>
          </ul>
        </div>
        <div>
          <em>
            (The 7-day wait allows time for people to scan codes and convert
            after receiving a flyer).
          </em>
          <br />
          <br />
          <strong>Why Might a Rating Be Missing?</strong>
          <br /> There are two main reasons you might not see a rating yet:
        </div>
        <div>
          <ol>
            <li>
              <strong>It's Too Soon:</strong> Ratings only appear{' '}
              <strong>7 days after</strong> the mission was completed. Please
              check back after this period.
            </li>
            <li>
              <strong>Incomplete Mission Information:</strong> The system needs
              all the necessary details to calculate the rating. Check if the
              mission setup is complete, including:
              <br />
              <ul>
                <li>City, Campaign, and Location information.</li>
                <li>A flyer pack correctly assigned to the mission.</li>
                <li>
                  Confirmation that flyers were distributed (checkout process
                  completed).
                </li>
              </ul>
            </li>
          </ol>
        </div>
        <div>
          If it's been more than 7 days and all information seems correct,
          please contact support.
        </div>
      </div>
    }
    uniqueId="rating-global-planing"
    element={
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <span>Rating</span> <QuestionMark style={{ marginRight: 4 }} />
      </span>
    }
  />
)

const groupHover = (groupId, message, element) => {
  return (
    <HoverWrapper
      title={`Group ID - ${groupId}`}
      hoverText={<div>{message}</div>}
      uniqueId="hover-group"
      element={element}
      placement="right"
    />
  )
}

const generateGroupColorMap = data => {
  const colors = ['#6DD9BA', '#5700D1', '#0F8233', '#CAD100']

  const groupColorMap = {}
  let colorIndex = 0

  data.forEach((item, index, arr) => {
    const groupInfo = item[0]
    if (groupInfo) {
      const [groupId] = groupInfo.split('-')
      if (!groupColorMap[groupId]) {
        // Ensure the new color is not the same as the previous item's color
        let assignedColor
        let attempts = 0
        do {
          assignedColor = colors[colorIndex % colors.length]
          colorIndex++
          attempts++
        } while (
          index > 0 &&
          arr[index - 1].groupId &&
          groupColorMap[arr[index - 1].groupId] === assignedColor &&
          attempts < colors.length
        )

        groupColorMap[groupId] = assignedColor
      }
    }
  })

  return groupColorMap
}

const Table = props => {
  const {
    classes,
    actions,
    footer,
    data = [],
    disableActionColumnIndexes = [],
    highlightColumnsIndexes = [],
    isHoverRating,
    cypress_id,
    baRedirectActions,
    disableHover,
    disableOpacityColumnsIndexes = [],
    isNeedToShowGroupedMissions,
  } = props
  const handleAction = (rowIndex, columnIndex) => {
    if (
      !actions ||
      !actions[rowIndex] ||
      disableActionColumnIndexes.includes(columnIndex)
    ) {
      return
    }

    return actions[rowIndex]()
  }
  const columnNames = props.header.map(item => item.label)
  const BaColumnIndex = columnNames.indexOf('BA')

  const handleBaAction = rowIndex => {
    if (!baRedirectActions || !baRedirectActions[rowIndex]) {
      return
    }

    return baRedirectActions[rowIndex]()
  }

  const colorsHashMap = isNeedToShowGroupedMissions
    ? generateGroupColorMap(data)
    : {}

  return (
    <TableC
      className={classnames(classes.table, 'Table')}
      cypress_id={cypress_id}
      style={disableHover ? { cursor: 'auto' } : {}}
    >
      {props.header && props.header.length ? (
        <thead className={classnames(classes.tableHeader, 'Table-tableHeader')}>
          <tr>
            {props.header.map(headerProps => {
              return isHoverRating && headerProps.label === 'RATING' ? (
                <SortableHeader
                  key={`TableC-thead-SortableHeader-rating`}
                  style={{
                    cursor: 'inherit',
                  }}
                  cypress_id={`${cypress_id}-header`}
                >
                  {rating}
                </SortableHeader>
              ) : (
                <SortableHeader
                  key={`TableC-thead-SortableHeader-${headerProps.label}`}
                  style={{
                    cursor: headerProps.onSortDirectionChange
                      ? 'pointer'
                      : 'inherit',
                  }}
                  onClick={headerProps.onSortDirectionChange}
                  onTouchTap={headerProps.onSortDirectionChange}
                  direction={headerProps.sortDirection}
                  cypress_id={`${cypress_id}-header`}
                >
                  {headerProps.label}
                </SortableHeader>
              )
            })}
          </tr>
        </thead>
      ) : null}
      <tbody>
        {data && data.length > 0
          ? data.map((row, rowIndex) => {
              const groupedData = isNeedToShowGroupedMissions ? row[0] : null
              const rowData = isNeedToShowGroupedMissions ? row.slice(1) : row
              return (
                <Tr
                  className={classnames({
                    [classes.rowWarning]: highlightColumnsIndexes.includes(
                      rowIndex,
                    ),
                  })}
                >
                  {rowData.map((item, columnIndex) => {
                    if (
                      !columnIndex &&
                      isNeedToShowGroupedMissions &&
                      groupedData
                    ) {
                      const [groupId, message] = groupedData.split('-')
                      return (
                        <Td
                          onClick={
                            BaColumnIndex && BaColumnIndex === columnIndex
                              ? handleBaAction.bind(this, rowIndex)
                              : handleAction.bind(this, rowIndex, columnIndex)
                          }
                          onTouchTap={handleAction.bind(
                            this,
                            rowIndex,
                            columnIndex,
                          )}
                          cypress_id={`${cypress_id}-value`}
                          className={classnames(classes.firstCellContainer, {
                            [classes.noOpacity]: disableOpacityColumnsIndexes.includes(
                              columnIndex,
                            ),
                          })}
                        >
                          {item}
                          <div className={classes.groupedDataOverlay}>
                            {groupHover(
                              groupId,
                              message,
                              <div
                                className={classes.hoverLine}
                                style={{
                                  background:
                                    colorsHashMap[groupId] || 'initial',
                                }}
                              />,
                            )}
                          </div>
                        </Td>
                      )
                    }
                    // Всі інші випадки — звичайна клітинка
                    return (
                      <Td
                        onClick={
                          BaColumnIndex && BaColumnIndex === columnIndex
                            ? handleBaAction.bind(this, rowIndex)
                            : handleAction.bind(this, rowIndex, columnIndex)
                        }
                        onTouchTap={handleAction.bind(
                          this,
                          rowIndex,
                          columnIndex,
                        )}
                        cypress_id={`${cypress_id}-value`}
                        className={classnames({
                          [classes.noOpacity]: disableOpacityColumnsIndexes.includes(
                            columnIndex,
                          ),
                        })}
                      >
                        {item}
                      </Td>
                    )
                  })}
                </Tr>
              )
            })
          : null}
      </tbody>
      {footer && footer.length ? (
        <tfoot>
          {footer.map(row => (
            <Tr>
              {row.map(value => (
                <Td cypress_id={`${cypress_id}-footer-value`}>{value}</Td>
              ))}
            </Tr>
          ))}
        </tfoot>
      ) : null}
    </TableC>
  )
}

const StyledTable = injectStyles(styles)(Table)

export default StyledTable
